import React from "react";
import { Phone, Mail, Code2, Scale } from "lucide-react";

/**
 * Global footer used across all public pages.
 * Credits HITECH SOLUTIONS SARL as the designer/developer
 * and provides a visible contact for commercial inquiries.
 */
export default function Footer({ variant = "light" }) {
  const dark = variant === "dark";
  const base = dark
    ? "bg-[#0D1B2A] text-white/80"
    : "bg-[#F8F9FA] text-foreground/70";
  const borderTop = dark ? "border-white/10" : "border-primary/10";
  const accentText = dark ? "text-white" : "text-primary";
  const mono = dark ? "text-white/50" : "text-foreground/50";

  return (
    <footer className={`border-t ${borderTop} ${base} py-10`} data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-8 md:gap-10">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-primary text-white flex items-center justify-center rounded-sm">
              <Scale size={15} />
            </div>
            <div className="leading-tight">
              <div className={`font-display text-sm font-semibold ${accentText}`}>Lexihab</div>
              <div className={`font-mono text-[10px] uppercase tracking-widest ${mono}`}>
                Conseiller juridique · Cameroun
              </div>
            </div>
          </div>
          <p className={`text-xs leading-relaxed ${dark ? "text-white/60" : "text-foreground/60"} max-w-xs`}>
            Assistant juridique spécialisé en droit de l'urbanisme, de l'habitat et du foncier au Cameroun.
            Réponses sourcées, citations exactes, bilingue FR/EN.
          </p>
        </div>

        {/* Quick info / legal */}
        <div>
          <div className={`font-mono text-[10px] uppercase tracking-widest ${mono} mb-3`}>
            À propos
          </div>
          <ul className={`text-xs space-y-1.5 ${dark ? "text-white/70" : "text-foreground/70"}`}>
            <li>
              Plateforme indépendante de conseil juridique informatif — ne remplace pas un avocat inscrit au barreau.
            </li>
            <li>Paiements sécurisés MTN Mobile Money · Orange Money (PawaPay).</li>
            <li>Sources : textes officiels camerounais consultés publiquement.</li>
          </ul>
        </div>

        {/* Agency / contact */}
        <div>
          <div className={`font-mono text-[10px] uppercase tracking-widest ${mono} mb-3`}>
            Conception & réalisation
          </div>
          <div className="flex items-start gap-3 mb-3">
            <div className={`w-8 h-8 ${dark ? "bg-accent text-[#0D1B2A]" : "bg-primary/10 text-primary"} rounded-sm flex items-center justify-center shrink-0 mt-0.5`}>
              <Code2 size={14} strokeWidth={1.8} />
            </div>
            <div className="leading-tight">
              <div className={`font-display text-sm font-semibold ${accentText}`}>HITECH SOLUTIONS SARL</div>
              <div className={`font-mono text-[10px] uppercase tracking-wider ${mono} mt-0.5`}>
                Ingénierie logicielle · IA appliquée · Cameroun
              </div>
            </div>
          </div>
          <a
            href="tel:+237675237270"
            className={`inline-flex items-center gap-2 text-xs font-medium ${dark ? "text-white hover:text-accent" : "text-primary hover:text-accent-dark"} transition-colors`}
            data-testid="footer-contact-phone"
          >
            <Phone size={12} strokeWidth={2} />
            +237 675 23 72 70
          </a>
        </div>
      </div>

      <div className={`max-w-7xl mx-auto px-6 md:px-10 mt-8 pt-6 border-t ${dark ? "border-white/10" : "border-primary/10"} flex flex-col md:flex-row md:items-center justify-between gap-3`}>
        <div className={`font-mono text-[10px] uppercase tracking-widest ${mono}`}>
          © 2026 Lexihab · Tous droits réservés
        </div>
        <div className={`font-mono text-[10px] ${mono}`}>
          Conçu et réalisé par{" "}
          <span className={`font-semibold ${accentText}`}>HITECH SOLUTIONS SARL</span>
          {" · "}
          <a href="tel:+237675237270" className="hover:underline">+237 675 23 72 70</a>
        </div>
      </div>
    </footer>
  );
}
